import { Component, OnInit, OnDestroy, ViewChild } from "@angular/core";
import { BehaviorSubject, Subject, merge } from 'rxjs';
import {debounceTime, distinctUntilChanged, takeUntil, throttleTime, filter, tap, map } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup } from '@angular/forms';
import { TenantService, StreamService } from '../services';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
    styles: [`
        .largeFF {
            margin-left: 20px;
            margin-right: 20px;
        }
    `],
    template: `
        <mat-card [formGroup]="form" style="margin: 15px;">
            <mat-card-content>
                <mat-form-field fxFlex="40" *ngIf="form.get('searchMethod').value !== 'tags'">
                    <input matInput formControlName="searchTerm" placeholder="Search Term">
                </mat-form-field>

                <mat-form-field fxFlex="40" *ngIf="form.get('searchMethod').value === 'tags'">
                    <mat-chip-list #tagList>
                            <mat-chip *ngFor="let tag of selectedTags" [removable]="true" (removed)="removeTag(tag)">
                                {{tag.label}}<mat-icon matChipRemove>cancel</mat-icon>
                            </mat-chip>
                            <input placeholder="Select tag..."
                                   #tagSearchControl
                                   formControlName="tagSearch"    
                                   [matChipInputFor]="tagList"
                                   [matAutocomplete]="tagAuto"/>                            
                            <mat-autocomplete #tagAuto="matAutocomplete"
                                (optionSelected)="addTag($event.option.value)"
                                [displayWith]="tagDisplay"
                                required>
                                <mat-option *ngFor="let tag of filteredStreamTag$ | async" [value]="tag">{{ tag.label }}</mat-option>
                            </mat-autocomplete>
                    </mat-chip-list>
                </mat-form-field>

                <mat-form-field fxFlexOffset="5" fxFlex="25">
                    <mat-select formControlName="searchMethod" placeholder="Search Method">
                        <mat-option value="default">Title & Description</mat-option>
                        <mat-option value="recommend">Article Content</mat-option>
                        <mat-option value="tags">Tags</mat-option>
                    </mat-select>
                </mat-form-field>

            </mat-card-content>

            <mat-card-actions>
                <button mat-raised-button color="primary" 
                                          [disabled]="noTouching || !selectedStreams.length" 
                                          (click)="save()">Save and Add ({{selectedStreams.length}})</button>
                <button mat-button [routerLink]="['../']">Cancel</button>
            </mat-card-actions>
        </mat-card>
        <div style="max-height: calc(100vh - 351px); overflow-y: auto;" 
             [ngClass]="{ largeFF: !isPhone}"
             infiniteScroll 
             [infiniteScrollThrottle]="500"
             [scrollWindow]="false"
             (scrolled)="nextPage()">
            <div fxLayoutAlign="center center"><spinner *ngIf="noTouching"></spinner></div>
            <table mat-table *ngIf="!noTouching" [dataSource]="stream$" fxFlex style="width: 100%">
                <ng-container matColumnDef="sources">
                    <th mat-header-cell *matHeaderCellDef>Sources</th>
                    <td mat-cell *matCellDef="let row" style="overflow: hidden; white-space: nowrap;">
                        <div style="text-overflow: ellipsis; overflow: hidden; max-width: calc(100vw - 200px)" [matTooltip]="row.title">{{row.title}}</div>
                    </td>
                </ng-container>
                <ng-container matColumnDef="type">
                    <th mat-header-cell *matHeaderCellDef>Type</th>
                    <td mat-cell *matCellDef="let row" >
                        <mat-icon [fontSet]="types[row.type].set" [fontIcon]="types[row.type].icon" [matTooltip]="types[row.type].label"></mat-icon>
                    </td>
                </ng-container>
                <ng-container matColumnDef="actions">
                    <th mat-header-cell *matHeaderCellDef></th>
                    <td mat-cell *matCellDef="let row">
                        <span *ngIf="!streamIds?.includes(row.id)">
                            <button *ngIf="!row.isSourceStream && !selectedStreams.includes(row.id)" mat-icon-button (click)="addSource(row.id)" style="float: right;">
                                <i class="material-icons">add</i>
                            </button>
                            <button *ngIf="!row.isSourceStream && selectedStreams.includes(row.id)" mat-icon-button (click)="removeSource(row.id)" style="float: right;">
                                <i class="material-icons">remove</i>
                            </button>
                        </span>
                    </td>
                </ng-container>
                <tr mat-header-row *matHeaderRowDef="['sources', 'type', 'actions']"></tr>
                <tr mat-row *matRowDef="let row; columns: ['sources', 'type', 'actions'];"></tr>
            </table>
            <div><spinner *ngIf="pageFetching" fxFlexOffset="40"></spinner></div>
        </div>
        <h3 *ngIf="!pageFetching && !noTouching && stream$.value?.length === 0" style="text-align: center">
            No sources match your search criteria. <button mat-button (click)="clearSearch()">Clear search.</button>
        </h3>
    `
})
export class CatalogAddSourcesComponent implements OnInit, OnDestroy {
    private cleanupTime = new Subject<void>();
    @ViewChild('tagSearchControl') tagSearchControl;
    isPhone: boolean;
    form: FormGroup;
    param$ = new BehaviorSubject<{ page: number; rows: number; filterEmpty: boolean; search?: string; tagIds?: string[]; term?: string; type?: string }>(undefined);
    noTouching: boolean;
    pageFetching: boolean;
    totalItems: number;
    stream$ = new BehaviorSubject<any[]>([]);
    selectedStreams = [];
    selectedTags = []; 
    streamTags: any;
    filteredStreamTag$ = new BehaviorSubject<any[]>([]);
    types = StreamService.types;
    streamIds: string[];
    
    constructor(private breakpointObserver: BreakpointObserver,
                private fb: FormBuilder, 
                private snackBar: MatSnackBar,
                private route: ActivatedRoute,
                private router: Router,
                private streamService: StreamService,
                private tenantService: TenantService) {}

    ngOnInit(): void {
        this.form = this.fb.group({
            searchTerm: [''],
            searchMethod: [''],
            tagSearch: ['']
        });

        this.tenantService.getCatalogStreams(this.catalogId, { page: 0,  rows: 400 })
        .subscribe(res => this.streamIds = res.content.map(stream => stream.id));

        this.breakpointObserver.observe([Breakpoints.Handset]).pipe(takeUntil(this.cleanupTime)).subscribe(result => this.isPhone = result.matches);

        merge(
            this.form.get('searchTerm').valueChanges.pipe(debounceTime(500),distinctUntilChanged()),
            this.form.get('searchMethod').valueChanges,
        ).pipe(
            throttleTime(500),
            tap(() => this.stream$.next([])),
            takeUntil(this.cleanupTime),
        )
        .subscribe(() => this.param$.next(Object.assign({}, this.param$.value, { page: 0})));

        this.form.get('tagSearch').valueChanges
        .pipe(takeUntil(this.cleanupTime), filter(val => typeof val === 'string'))
        .subscribe(val => this.filterTags(val));

        this.param$
        .pipe(
            filter(params => !!params),
            takeUntil(this.cleanupTime)
        ).subscribe(params => {
            const searchType = this.form.get('searchMethod').value;
            const searchTerm = this.form.get('searchTerm').value;
            const isRecommended = searchType === 'recommend';
            const isTags = searchType === 'tags';
            
            this.noTouching = !this.stream$.value.length;
    
            //consistency, hobgoblins, you get the idea
            params.type = isRecommended ? 'item_search' : undefined;
            params.search = isRecommended ? searchTerm : undefined;
            params.term = isTags ? undefined : searchTerm;
    
            params.tagIds = isTags ? this.selectedTags.map(tag => tag.id) : undefined;
    
            if(isRecommended && !searchTerm){
                this.noTouching = false;
                this.stream$.next([]);
                this.totalItems = 0;
                return;
            }
    
            const searcher = isRecommended ?
                                this.streamService.getSharedRecommendedStreams(params) :
                                this.streamService.getSharedStreams(params);
    
            searcher.pipe(
                tap(() => this.noTouching = false),
                tap(() => this.pageFetching = false),
                tap(results => this.totalItems = results.totalElements),
                map(results => this.stream$.value.concat(results.content))
            ).subscribe(streams => this.stream$.next(streams));
        });

        this.form.patchValue({ searchMethod: 'default' });
        
        this.param$.next({ page: 0, rows: 40, filterEmpty: true});
        this.tenantService.getCatalogTags()
        .pipe(tap(tags => this.streamTags = tags))
        .subscribe(() => this.filterTags(''));
    }

    filterTags(val): void {
        const filteredTags = this.streamTags?.filter(tag => !this.selectedTags?.includes(tag) && tag.label.toLowerCase().includes(val.toLowerCase()));
        this.filteredStreamTag$.next(filteredTags);
    }

    addTag(tag): void {
        this.selectedTags.push(tag);
        this.form.get('tagSearch').setValue('');
        this.tagSearchControl.nativeElement.value = ''; // they can't all be hits. This patches a material issue of not clearing the underlying control
        this.stream$.next([]);
        this.param$.next(Object.assign({}, this.param$.value, { page: 0 }));
    }

    removeTag(tag): void {
        this.selectedTags.splice(this.selectedTags.findIndex(curTag => curTag === tag), 1);
        this.stream$.next([]);
        this.param$.next(Object.assign({}, this.param$.value, { page: 0 }));
    }

    tagDisplay(tag): string {
        return tag ? tag.label : '';
    }

    addSource(sourceId): void {
        this.selectedStreams.push(sourceId);
    }

    removeSource(sourceId): void {
        this.selectedStreams.splice(this.selectedStreams.findIndex(id => id === sourceId), 1);
    }

    nextPage(): void {
        if(this.totalItems > this.stream$.value?.length){
            this.pageFetching = true;
            this.param$.next(Object.assign({}, this.param$.value, { page: ++this.param$.value.page }));
        }
    }

    get catalogId(): string {
        return this.route.snapshot.parent.parent.params.catalogId;
    }

    save(): void {
        this.noTouching = true;
        this.streamService.addStreamsToCatalog(this.catalogId, this.selectedStreams).pipe(
            tap(() => this.snackBar.open('Sources Added.', 'OK', { duration: 3000}))
        ).subscribe(() => this.router.navigate(['../'], { relativeTo: this.route}));
    }

    clearSearch(): void {
        this.form.get('searchTerm').setValue('');        
    }

    ngOnDestroy(): void {
        this.param$.complete();
        this.stream$.complete();
        this.filteredStreamTag$.complete();
        this.cleanupTime.next();
        this.cleanupTime.complete();
    }
}
